import {
  AlignItems,
  Body1,
  Box,
  Breakpoint,
  Color,
  Display,
  Display1,
  FlexDirection,
  FontWeight,
  Text,
  Theme,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { UnsubscribedHeroData } from '@library/api-customer-engagement-experience'

import dishCollageDesktop from './assets/dish-collage-desktop.webp'
import dishCollageMobile from './assets/dish-collage-mobile.webp'
import dishCollageTablet from './assets/dish-collage-tablet.webp'
import { EmptyStateHeroOptionCard } from './EmptyStateHeroOptionCard'

type EmptyStateHeroProps = {
  data: UnsubscribedHeroData
  onActionClick: (intent: string) => void
}

const SCRIM_RADIUS = '4px'
const SCRIM_OPACITY = 0.9

const hexToRgba = (hex: string, opacity: number) => {
  const value = hex.replace('#', '')
  const r = parseInt(value.substring(0, 2), 16)
  const g = parseInt(value.substring(2, 4), 16)
  const b = parseInt(value.substring(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

// box-decoration-break clones the backdrop per wrapped line; rgba keeps text fully opaque;
// margin cancels padding below so text stays aligned with siblings.
const cloneScrimStyle = (theme: Theme) => ({
  display: 'inline' as const,
  backgroundColor: hexToRgba(theme.color(Color.ColdGrey_800)!, SCRIM_OPACITY),
  borderRadius: SCRIM_RADIUS,
  padding: '2px 8px',
  margin: '-2px -8px',
  boxDecorationBreak: 'clone' as const,
  WebkitBoxDecorationBreak: 'clone' as const,
})

const Headline = withCitrusTheme(Display1, (theme) => ({
  fontWeight: 800,
  textTransform: 'uppercase',
  ...cloneScrimStyle(theme),
}))

const WelcomeText = withCitrusTheme(Body1, (theme) => cloneScrimStyle(theme))

const HeroContainer = withCitrusTheme(Box, () => ({
  overflow: 'hidden',
  padding: '16px',
  position: 'relative',
  '@media (min-width: 480px)': {
    padding: '32px',
  },
}))

const CollageImage = withCitrusTheme('img', () => ({
  position: 'absolute',
  top: 0,
  right: 0,
  height: 'auto',
  width: 'auto',
  maxHeight: '226px',
  [`@media (min-width: ${Breakpoint.Medium})`]: {
    maxHeight: '319px',
  },
  [`@media (min-width: ${Breakpoint.Large})`]: {
    maxHeight: '274px',
  },
  pointerEvents: 'none',
  userSelect: 'none',
}))

const Content = withCitrusTheme(Box, () => ({
  position: 'relative',
}))

const Scrim = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '-2px -8px',
    borderRadius: SCRIM_RADIUS,
    backgroundColor: theme.color(Color.ColdGrey_800),
    opacity: SCRIM_OPACITY,
    zIndex: -1,
  },
}))

export const EmptyStateHero = ({ data, onActionClick }: EmptyStateHeroProps) => (
  <HeroContainer
    bg={Color.ColdGrey_800}
    data-testid="empty-state-hero"
    style={{ borderRadius: '4px' }}
  >
    <picture>
      <source media={`(min-width: ${Breakpoint.Large})`} srcSet={dishCollageDesktop} />
      <source media={`(min-width: ${Breakpoint.Medium})`} srcSet={dishCollageTablet} />
      <CollageImage
        alt=""
        aria-hidden="true"
        data-testid="empty-state-hero-collage"
        src={dishCollageMobile}
      />
    </picture>
    <Content
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      zIndex={ZIndex.Default}
    >
      <Box
        alignItems={AlignItems.FlexStart}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={2}
      >
        <Box>
          <WelcomeText
            color={Color.White}
            fontWeight={[FontWeight.Normal, FontWeight.Bold]}
            size={[1, null, 9]}
          >
            {data.welcome}
          </WelcomeText>
        </Box>
        <Box>
          <Headline color={Color.White} size={[11, null, 14]}>
            {data.headline}
          </Headline>
        </Box>
        {data.offer ? (
          <Box
            alignItems={[AlignItems.FlexStart, null, AlignItems.Center]}
            display={Display.Flex}
            flexDirection={[FlexDirection.Column, null, FlexDirection.Row]}
            gap={2}
            style={{ marginTop: '8px', marginBottom: '4px' }}
          >
            <Box alignItems={AlignItems.Center} display={Display.Flex} gap={2}>
              <Box
                alignItems={AlignItems.Center}
                bg={Color.Success_900}
                display={Display.Flex}
                paddingH={2}
                style={{ borderRadius: '4px' }}
              >
                <Text color={Color.White} fontWeight={FontWeight.Bold} size={[4, null, 9]}>
                  {data.offer.badge}
                </Text>
              </Box>
              {data.offer.label ? (
                <Body1 color={Color.White} fontWeight={FontWeight.Bold} size={[4, null, 9]}>
                  {data.offer.label}
                </Body1>
              ) : null}
            </Box>
            {data.offer.expiry_label ? (
              <Box alignItems={AlignItems.Center} display={Display.Flex} gap={2}>
                <Box aria-hidden display={[Display.None, null, Display.Block]}>
                  <Text color={Color.ColdGrey_200} size={[2, null, 5]}>
                    •
                  </Text>
                </Box>
                <Scrim>
                  <Body1 color={Color.White} fontWeight={FontWeight.Normal} size={[2, null, 5]}>
                    {data.offer.expiry_label}
                  </Body1>
                </Scrim>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Box>
      <Box
        data-testid="empty-state-hero-options"
        display={Display.Flex}
        flexDirection={[FlexDirection.Column, null, null, FlexDirection.Row]}
        gap={[4, null, null, 3]}
      >
        {data.options.map((option) => (
          <Box flexBasis="0" flexGrow={1} key={option.action.intent}>
            <EmptyStateHeroOptionCard
              option={option}
              onClick={() => onActionClick(option.action.intent)}
            />
          </Box>
        ))}
      </Box>
    </Content>
  </HeroContainer>
)
